import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BlogPost } from '../components';
import './SearchPage.css';

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.get('q') || '';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function searchPosts() {
      if (!queryString.trim()) {
        setPosts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch all published posts
        const q = query(
          collection(db, 'posts'),
          where('published', '==', true),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const allPosts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Client-side search across all fields
        const searchTerms = queryString.toLowerCase().split(/\s+/);

        const filteredPosts = allPosts.filter((post) => {
          const searchableText = [
            post.title || '',
            post.content || '',
            post.excerpt || '',
          ].join(' ').toLowerCase();

          // All search terms must match
          return searchTerms.every((term) => searchableText.includes(term));
        });

        setPosts(filteredPosts);
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to search posts');
      } finally {
        setLoading(false);
      }
    }

    searchPosts();
  }, [queryString]);

  if (!queryString.trim()) {
    return (
      <div className="search-page">
        <h1>Search</h1>
        <p className="search-page__empty">Enter a search term to find posts.</p>
        <Link to="/" className="btn btn-secondary">Back to Home</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="search-page">
        <h1>Searching...</h1>
        <p>Looking for "{queryString}"</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="search-page">
        <h1>Search Error</h1>
        <p className="search-page__error">{error}</p>
        <Link to="/" className="btn btn-secondary">Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="search-page">
      <h1>Search Results</h1>
      <p className="search-page__summary">
        {posts.length} {posts.length === 1 ? 'result' : 'results'} for "{queryString}"
      </p>

      {posts.length === 0 ? (
        <div className="search-page__no-results">
          <p>No posts found matching your search.</p>
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
        </div>
      ) : (
        <div className="search-page__results">
          {posts.map((post) => (
            <BlogPost key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
}
